import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, Redirect } from "react-router-dom";
import { Box, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import moment from "moment";
import Navbar from "components/Navbar";
import DiagnosticsBar from "components/Masterbar/DiagnosticsBar";
import DarkLayout from "components/Shared/DarkLayout";
import {
  getFeedback,
  selectDiagnosticsLoading,
  selectSelectedFeedback,
  setSelectedFeedback,
} from "redux/slices/diagnosticsSlice";
import { selectUser } from "redux/slices/userSlice";
import { PATHS } from "util/appConstants";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _basicdetailhead: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: "41px",
  },
  _field: {
    marginRight: "48px",
    marginBottom: "16px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
  },
  _detail: {
    color: "#F5F5F5",
    font: "normal normal normal 18px/24px Roboto",
  },
  meta: {
    color: "#ADADAD",
    fontSize: 13,
    whiteSpace: "pre-wrap",
    maxHeight: 360,
    overflow: "auto",
    background: "#1F1F1F",
    padding: 12,
    borderRadius: 4,
    marginTop: 8,
  },
  screenshot: {
    maxWidth: "100%",
    maxHeight: 480,
    borderRadius: 4,
    marginTop: 8,
  },
});

const isDiagnosticsVisible = (user) =>
  user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true";

const FeedbackDetail = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const user = useSelector(selectUser);
  const loading = useSelector(selectDiagnosticsLoading);
  const feedback = useSelector(selectSelectedFeedback);

  useEffect(() => {
    if (!isDiagnosticsVisible(user) || !id) {
      return;
    }
    dispatch(getFeedback(id));
    return () => {
      dispatch(setSelectedFeedback(null));
    };
  }, [dispatch, user, id]);

  if (!isDiagnosticsVisible(user)) {
    return <Redirect to={PATHS.admin.supplierGroups} />;
  }

  return (
    <>
      <Navbar />
      <DiagnosticsBar />
      <DarkLayout doublebar loading={loading || !feedback}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {t("Feedback")} #{feedback?.id}
          </Typography>
          <Box className={classes._buttonbox} component="div">
            <CloseIcon
              onClick={() => history.push(PATHS.diagnostics.web)}
              className={classes._edit}
            />
            <Typography component="span" className={clsx(classes._edittext, "edittag")}>
              {t("Close")}
            </Typography>
          </Box>
        </div>

        <div className={classes._basicdetailhead}>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Feedback Type")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {feedback?.type ? t(feedback.type) : "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("User")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {feedback?.user_name || "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("E-Mail")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {feedback?.user_email || "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Supplier Location")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {feedback?.supplier_location_name || "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Created")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {feedback?.created_at
                ? moment(feedback.created_at).format("DD.MM.YYYY HH:mm")
                : "-"}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Email Sent")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {feedback?.email_sent ? t("Yes") : t("No")}
            </Typography>
          </div>
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Message")}
        </Typography>
        <Typography className={classes.meta} component="pre">
          {feedback?.message || "-"}
        </Typography>

        {feedback?.screenshot_url && (
          <>
            <Typography className={classes._infoheading} variant="h5">
              {t("Screenshot")}
            </Typography>
            <img
              src={feedback.screenshot_url}
              alt={t("Screenshot")}
              className={classes.screenshot}
            />
          </>
        )}

        {feedback?.console_log && (
          <>
            <Typography className={classes._infoheading} variant="h5">
              {t("Console Log")}
            </Typography>
            <Typography className={classes.meta} component="pre">
              {feedback.console_log}
            </Typography>
          </>
        )}

        {feedback?.network_log && (
          <>
            <Typography className={classes._infoheading} variant="h5">
              {t("Network Log")}
            </Typography>
            <Typography className={classes.meta} component="pre">
              {feedback.network_log}
            </Typography>
          </>
        )}
      </DarkLayout>
    </>
  );
};

export default FeedbackDetail;
